import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { helpSectionForPath } from "@/lib/helpContent";
import { applyTheme } from "@/lib/themeApplier";
import {
  LayoutDashboard, CalendarDays, Package, FileText, Users, LogOut, Globe, CalendarOff, Inbox, CircleHelp,
  FileSignature, Images, ExternalLink, Star, Menu, X, Sparkles, Building2,
  ChevronRight, Search, Bell, ChevronDown, Sun, Moon, User as UserIcon, CreditCard,
} from "lucide-react";
import LumetierWordmark from "@/components/LumetierWordmark";
import CopilotPill from "@/components/ai/CopilotPill";
import MobileTabBar from "@/components/MobileTabBar";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationsBell from "@/components/admin/NotificationsBell";
import useSwipeNav from "@/hooks/useSwipeNav";

// Mobile swipe-nav cycle — mirrors the visible routes in MobileTabBar.
// Swipe-left from the rightmost route opens the AI Copilot instead of
// looping back to Home (that wrap-around always feels jarring).
const SWIPE_ROUTES = ["/admin", "/admin/bookings", "/admin/galleries", "/admin/customers"];

// ── Navigation groups ─────────────────────────────────────────────────────────
const navGroups = [
  {
    label: "Workflow",
    items: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin/bookings", label: "Bookings", icon: CalendarDays },
      { to: "/admin/availability", label: "Availability", icon: CalendarOff },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/contracts", label: "Contracts", icon: FileSignature },
    ],
  },
  {
    label: "Clients & Content",
    items: [
      { to: "/admin/inquiries", label: "Inquiries", icon: Inbox, badge: "inquiries" },
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/reviews", label: "Reviews", icon: Star },
      { to: "/admin/galleries", label: "Galleries", icon: Images },
      { to: "/admin/services", label: "Services", icon: Package },
    ],
  },
  {
    label: "Studio",
    items: [
      { to: "/admin/site", label: "Site Settings", icon: Globe },
      { to: "/admin/waitlist", label: "Waitlist", icon: Sparkles, platformOnly: true },
      { to: "/admin/studio-invites", label: "Studios", icon: Building2, platformOnly: true },
    ],
  },
];

const PLATFORM_OWNER_STUDIO_ID = "allure-image";

// Sidebar badge: unanswered inquiries. Refreshes every minute and whenever the
// admin navigates (e.g. after replying inside /admin/inquiries).
function useNewInquiryCount(pathname) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/admin/inquiries/counts").then((r) => alive && setCount(r.data.new || 0)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);
  return count;
}
const allItems = navGroups.flatMap((g) => g.items);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const newInquiries = useNewInquiryCount(location.pathname);
  const helpSection = location.pathname.startsWith("/admin/help") ? null : helpSectionForPath(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  // Per-user theme preference. Default to dark (the new editorial canvas);
  // user can flip back to the light cream surface via the top-bar toggle.
  // Initial paint reads localStorage (fast path, no /me round-trip yet); once
  // /auth/me resolves with a server-stored `chrome_theme` we sync to that so
  // the preference follows the admin across devices.
  const [dark, setDark] = useState(() => {
    try {
      const v = localStorage.getItem("admin_theme");
      return v === null ? true : v === "dark";
    } catch { return true; }
  });

  // Billing surface is gated by the backend's `BILLING_ENABLED` flag. In
  // production we keep the entire "Billing & plan" menu item hidden so
  // tenants never see even a hint of what's coming until we're ready to
  // flip the flag. Fetch once on mount, cache in state.
  const [billingEnabled, setBillingEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/pricing")
      .then((r) => { if (!cancelled) setBillingEnabled(Boolean(r?.data?.enabled)); })
      .catch(() => { if (!cancelled) setBillingEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  // Server-side sync: when the auth context resolves the user record, hydrate
  // `dark` from `user.chrome_theme` if it differs from local state. Skips on
  // first mount when localStorage already matched the server (steady state).
  // The dependency on `user?.chrome_theme` keeps this from re-firing on every
  // unrelated auth update.
  useEffect(() => {
    const serverPref = user?.chrome_theme;
    if (!serverPref) return;
    const serverDark = serverPref === "dark";
    setDark((prev) => (prev === serverDark ? prev : serverDark));
  }, [user?.chrome_theme]);

  useEffect(() => {
    try { localStorage.setItem("admin_theme", dark ? "dark" : "light"); }
    catch { /* ignore */ }
    // Broadcast the change so other admin surfaces (ThemeSection's live
    // applyTheme preview, etc.) can re-evaluate whether to push the tenant's
    // dark palette onto <html> without needing prop drilling.
    window.dispatchEvent(new CustomEvent("lumetier:admin-chrome-changed", { detail: { dark } }));
  }, [dark]);

  // User-initiated toggle: flip local state AND persist to server. Kept out
  // of the generic persist effect above so initial-mount renders (which may
  // briefly disagree with the server before the sync effect catches up) don't
  // accidentally overwrite the user's stored preference.
  function toggleAdminChrome() {
    setDark((prev) => {
      const next = !prev;
      if (user?.id) {
        api.patch("/auth/me/preferences", { chrome_theme: next ? "dark" : "light" })
          .catch(() => { /* non-blocking; localStorage still works as a fallback */ });
      }
      return next;
    });
  }

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Manifest "Ask Lumetier" shortcut → /admin?copilot=1 auto-opens panel
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("copilot") === "1") setCopilotOpen(true);
  }, [location.search]);

  // Close user menu when clicking outside
  useEffect(() => {
    function onClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [userMenuOpen]);

  useEffect(() => {
    // Only push the tenant's resolved palette onto <html> as inline CSS vars
    // when the admin is in DARK mode. In LIGHT mode we want the :root cream
    // defaults (defined in index.css) to win — inline styles on <html> would
    // otherwise beat both :root and the .admin-dark class rule and pin the
    // admin chrome to the tenant's dark canvas regardless of the toggle.
    // When the toggle flips back to dark, the effect re-runs and re-applies.
    if (!dark) return;
    let cleanup = () => {};
    let cancelled = false;
    async function loadTheme() {
      try {
        const { data } = await api.get("/admin/site");
        if (cancelled) return;
        cleanup();
        cleanup = applyTheme({ themeId: data.theme_id || "editorial", overrides: data.theme_overrides || {} });
      } catch { /* not logged in yet */ }
    }
    loadTheme();
    const onChange = () => loadTheme();
    window.addEventListener("lumetier:theme-changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("lumetier:theme-changed", onChange);
      cleanup();
    };
  }, [dark]);

  const currentItem =
    (location.pathname.startsWith("/admin/help") ? { to: "/admin/help", label: "Help", icon: CircleHelp } : null) ||
    allItems.find((it) =>
      it.end ? location.pathname === it.to : location.pathname.startsWith(it.to)
    ) || allItems[0];

  const allowedRoutes = SWIPE_ROUTES.filter(
    (r) => allItems.some((it) => it.to === r) || r === "/admin"
  );
  // Resolve where we are in the swipe cycle. Pages that aren't part of the
  // mobile tab bar (e.g. /admin/site, /admin/profile) fall through and the
  // gesture is a no-op for them — by design, swiping off Site Settings into
  // Bookings would lose unsaved edits.
  const swipeIdx = allowedRoutes.findIndex((r) =>
    r === "/admin" ? location.pathname === r : location.pathname.startsWith(r)
  );
  useSwipeNav({
    enabled: swipeIdx >= 0 && !mobileOpen && !copilotOpen,
    onSwipeLeft: () => {
      if (swipeIdx < 0) return;
      if (swipeIdx < allowedRoutes.length - 1) {
        navigate(allowedRoutes[swipeIdx + 1]);
      } else {
        // Already at the rightmost tab — swipe-left opens the AI Copilot,
        // matching the 5th icon in MobileTabBar.
        setCopilotOpen(true);
      }
    },
    onSwipeRight: () => {
      if (swipeIdx > 0) navigate(allowedRoutes[swipeIdx - 1]);
    },
  });

  const initials = (user?.email || "A")[0].toUpperCase();
  const handle = user?.email?.split("@")[0] || "studio";

  // "View public site" — sends the studio owner to their OWN tenant page
  // (`/s/<slug>`), not the Lumetier marketing root. Without this, a tenant
  // editing their site clicks "View site" and lands on the SaaS landing page
  // — confusing and the most common reason "my edits aren't showing up".
  // The Allure founder studio is exempt because Allure's site lives at the
  // root in the Allure-domain front door.
  const isPlatformOwner = user?.studio_id === PLATFORM_OWNER_STUDIO_ID;
  const ownerSlug = user?.studio_slug || (user?.studio_id && user.studio_id !== PLATFORM_OWNER_STUDIO_ID ? user.studio_id : null);
  const viewSiteHref = isPlatformOwner ? "/" : (ownerSlug ? `/s/${ownerSlug}` : "/");

  // ── Sidebar ────────────────────────────────────────────────────────────────
  const Sidebar = (
    <div className="admin-sidebar flex flex-col h-full bg-[#0A0908] text-stone-300 border-r border-white/[0.06]">
      {/* Brand header */}
      <div className="h-16 flex items-center px-6 border-b border-white/[0.06] shrink-0">
        <Link
          to="/"
          title="Back to main site"
          data-testid="admin-logo-home-link"
          className="flex items-center gap-3 group"
        >
          <span className="w-8 h-8 rounded-sm bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/30 flex items-center justify-center font-serif text-lg text-[hsl(var(--gold))] group-hover:bg-[hsl(var(--gold))]/20 transition-colors">
            L
          </span>
          <div className="flex flex-col gap-0.5 leading-none">
            <LumetierWordmark tone={dark ? "light" : "dark"} className="text-sm" testid="admin-sidebar-wordmark" />
            <span className={`text-[9px] uppercase tracking-[0.32em] font-medium ${dark ? "text-stone-500" : "text-stone-600"}`}>
              Studio Manager
            </span>
          </div>
        </Link>
      </div>

      {/* Grouped navigation */}
      <nav className="flex-1 px-3 py-6 overflow-y-auto space-y-7" aria-label="Studio Manager navigation">
        {navGroups.map((group) => {
          const items = group.items.filter(
            (it) => !it.platformOnly || user?.studio_id === PLATFORM_OWNER_STUDIO_ID
          );
          if (items.length === 0) return null;
          return (
            <div key={group.label}>
              <div className="px-3 mb-2.5 text-[9px] uppercase tracking-[0.32em] text-stone-600 font-semibold select-none">
                {group.label}
              </div>
              <ul className="space-y-0.5">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        end={it.end}
                        data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 text-[13px] transition-all duration-150 rounded-sm ${
                            isActive
                              ? "bg-white/[0.05] text-stone-100 border-l-2 border-[hsl(var(--gold))] pl-[10px] font-medium"
                              : "text-stone-400 hover:bg-white/[0.04] hover:text-stone-200 border-l-2 border-transparent"
                          }`
                        }
                      >
                        <Icon className="w-[15px] h-[15px] shrink-0 opacity-80" strokeWidth={1.25} />
                        {it.label}
                        {it.badge === "inquiries" && newInquiries > 0 && (
                          <span
                            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold tabular-nums"
                            data-testid="nav-inquiries-badge"
                          >
                            {newInquiries > 99 ? "99+" : newInquiries}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Footer — Ask Lumetier CTA + view site link */}
      <div className="px-3 pb-5 border-t border-white/[0.06] pt-4 shrink-0 space-y-3">
        <button
          onClick={() => setCopilotOpen(true)}
          className="w-full text-left bg-[hsl(var(--gold))]/[0.06] border border-[hsl(var(--gold))]/30 hover:border-[hsl(var(--gold))]/55 hover:bg-[hsl(var(--gold))]/[0.10] transition-colors p-3 rounded-sm group"
          data-testid="sidebar-ask-lumetier-cta"
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))] font-medium">
            Need help?
          </div>
          <div className="text-[11px] text-stone-400 leading-snug mt-1">
            Ask Lumetier AI assistant<br />for quick answers.
          </div>
          <div className="mt-2.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--gold))] font-medium">
            <Sparkles className="w-3 h-3" /> Ask Lumetier
          </div>
        </button>

        <NavLink
          to="/admin/help"
          className={({ isActive }) => `flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2 transition-colors ${isActive ? "text-stone-200" : "text-stone-500 hover:text-stone-200"}`}
          data-testid="admin-nav-help"
        >
          <CircleHelp className="w-3.5 h-3.5" /> Help &amp; how-to guides
        </NavLink>
        <Link
          to={viewSiteHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[10px] text-stone-500 hover:text-stone-200 transition-colors uppercase tracking-[0.22em] px-2"
          data-testid="back-to-site-link"
        >
          <ExternalLink className="w-3 h-3" /> View site
        </Link>
      </div>
    </div>
  );

  // ── Top bar ────────────────────────────────────────────────────────────────
  const TopBar = (
    <header
      className="sticky top-0 z-30 h-14 lg:h-16 px-3 sm:px-4 lg:px-10 border-b border-border/60 bg-background/85 backdrop-blur-md flex items-center gap-3 lg:gap-4"
      data-testid="admin-topbar"
    >
      {/* Mobile menu trigger */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation"
        className="lg:hidden inline-flex items-center justify-center w-9 h-9 -ml-1 text-stone-300 hover:text-stone-100 shrink-0"
        data-testid="admin-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Section eyebrow + current page */}
      <div className="hidden lg:flex flex-col leading-none">
        <span className="text-[9px] uppercase tracking-[0.32em] text-muted-foreground font-medium">
          Studio Manager
        </span>
        <span className="inline-flex items-baseline gap-3 mt-1.5">
          <span
            className="text-[15px] font-serif font-light text-foreground"
            data-testid="admin-topbar-current-section"
          >
            {currentItem.label}
          </span>
          {helpSection && (
            <Link
              to={`/admin/help#${helpSection.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="topbar-help-link"
            >
              <CircleHelp className="w-3 h-3" /> How this works
            </Link>
          )}
        </span>
      </div>

      {/* Mobile current-section label — chevron acts as visual anchor; section
          name sits beside it in a single editorial caps line that truncates
          gracefully on very narrow phones. */}
      <div className="lg:hidden flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-foreground/80 min-w-0">
        <ChevronRight className="w-3 h-3 opacity-40 shrink-0" />
        <span className="truncate" data-testid="admin-mobile-current-section">{currentItem.label}</span>
      </div>

      {/* Search — center-stretched · hairline editorial */}
      <div className="ml-auto hidden md:flex items-center relative max-w-sm w-full">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--gold-base)]/60 pointer-events-none" strokeWidth={1.25} />
        <input
          type="text"
          placeholder="Search the studio…"
          className="w-full bg-transparent border-b border-border/70 pl-10 pr-3 h-10 text-sm focus:outline-none focus:border-[hsl(var(--gold))]/60 transition-colors placeholder:font-serif placeholder:italic placeholder:text-muted-foreground/55 placeholder:text-[15px]"
          data-testid="admin-global-search"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.currentTarget.value.trim()) {
              navigate(`/admin/bookings?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
            }
          }}
        />
      </div>

      {/* Right cluster: theme toggle + notifications + avatar */}
      <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 ml-auto md:ml-0 shrink-0">
        <button
          onClick={toggleAdminChrome}
          aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          title={dark ? "Switch to light mode" : "Switch to dark mode"}
          className="w-9 h-9 lg:w-10 lg:h-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground border border-transparent hover:border-border rounded-sm transition-colors"
          data-testid="admin-theme-toggle"
        >
          {dark ? <Sun className="w-[17px] h-[17px]" strokeWidth={1.5} /> : <Moon className="w-[17px] h-[17px]" strokeWidth={1.5} />}
        </button>

        <NotificationsBell data-testid="admin-notifications-btn" />

        {/* Avatar + dropdown */}
        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2.5 px-1.5 h-10 hover:bg-card/60 transition-colors rounded-sm"
            data-testid="admin-user-menu-btn"
          >
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={handle}
                className="w-8 h-8 rounded-full object-cover ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-background"
                data-testid="admin-user-avatar-img"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--gold))]/15 ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-background flex items-center justify-center text-[hsl(var(--gold))] text-xs font-semibold">
                {initials}
              </div>
            )}
            <span className="hidden sm:inline text-xs font-medium text-foreground/90 max-w-[120px] truncate">
              {handle}
            </span>
            <ChevronDown className="hidden sm:block w-3.5 h-3.5 text-muted-foreground" />
          </button>

          {userMenuOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border shadow-xl shadow-black/40 rounded-sm overflow-hidden py-1.5 z-50"
              data-testid="admin-user-menu"
            >
              <div className="px-3.5 py-2.5 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Signed in as</div>
                <div className="text-xs text-foreground truncate mt-0.5" title={user?.email}>{user?.email}</div>
              </div>
              <Link
                to="/admin/profile"
                className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                data-testid="admin-user-menu-profile"
              >
                <UserIcon className="w-3.5 h-3.5" /> Your profile
              </Link>
              <Link
                to="/admin/site"
                className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                data-testid="admin-user-menu-settings"
              >
                <Globe className="w-3.5 h-3.5" /> Site settings
              </Link>
              {billingEnabled && (
                <Link
                  to="/admin/billing"
                  className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                  data-testid="admin-user-menu-billing"
                >
                  <CreditCard className="w-3.5 h-3.5" /> Billing &amp; plan
                </Link>
              )}
              <Link
                to={viewSiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                data-testid="admin-user-menu-view-site"
              >
                <ExternalLink className="w-3.5 h-3.5" /> View public site
              </Link>
              <button
                onClick={() => { logout(); navigate("/admin/login"); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-destructive transition-colors border-t border-border mt-1 pt-2.5"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  // Apply theme class to <body> so global CSS overrides (.admin-light) can
  // target hard-coded charcoal hexes inside Sidebar / EditorialDialog / etc.
  useEffect(() => {
    const cls = dark ? "admin-dark" : "admin-light";
    const other = dark ? "admin-light" : "admin-dark";
    document.body.classList.add(cls);
    document.body.classList.remove(other);
    return () => { document.body.classList.remove(cls); };
  }, [dark]);

  return (
    <div className={`flex h-screen overflow-hidden bg-background text-foreground ${dark ? "admin-dark" : ""}`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col admin-sidebar" data-testid="admin-sidebar">
        {Sidebar}
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="admin-drawer-backdrop"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-mobile-drawer"
        aria-hidden={!mobileOpen}
      >
        {Sidebar}
        {mobileOpen && (
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
            className="absolute top-4 right-3 inline-flex items-center justify-center w-8 h-8 text-stone-400 hover:text-stone-100"
            data-testid="admin-menu-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </aside>

      {/* Main column */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0 max-w-full">
        {TopBar}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-4 sm:px-8 lg:px-12 py-6 lg:py-10 pb-24 lg:pb-12 max-w-[1400px] mx-auto min-w-0">
            <Outlet />
          </div>
          {/* Editorial bottom status bar — hairline gold rule + studio signature */}
          <div className="hidden lg:flex items-center justify-between px-12 h-9 border-t border-[color:var(--gold-foil)]/25 text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 select-none" data-testid="admin-status-bar">
            <span>
              <span className="text-[color:var(--gold-base)]">·</span> {user?.studio_id === PLATFORM_OWNER_STUDIO_ID ? "Allure Image" : (user?.studio_slug || "studio")}
            </span>
            <span className="font-serif italic normal-case tracking-normal text-[12px]">crafted with Lumetier</span>
            <span>{new Date().getFullYear()} · ED. I</span>
          </div>
        </div>
      </main>

      <MobileTabBar onOpenCopilot={() => setCopilotOpen(true)} />
      <InstallPrompt />
      <CopilotPill forceOpen={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
